import { Link } from '@tanstack/react-router'
import { Box, Typography } from '@mui/material'
import { docNeighbours } from './nav'
import { Lede, MEASURE } from './prose'

interface DocPageProps {
  /** Must match the route path so prev/next can find its place. */
  path: string
  title: string
  lede: React.ReactNode
  /** Anchors for the "On this page" list. ids must match <Section id>. */
  sections: Array<{ id: string; title: string }>
  children: React.ReactNode
}

/**
 * Page frame: title, lede, on-this-page anchors, body, prev/next.
 *
 * The anchor list is passed in rather than scraped from the DOM. Scraping
 * needs an effect and a re-render, and gets the order wrong the moment a
 * diagram or aside sits between two headings.
 */
export function DocPage({ path, title, lede, sections, children }: DocPageProps) {
  const { prev, next } = docNeighbours(path)

  return (
    <Box>
      <Typography component="h1" sx={{
        fontSize: { xs: '1.85rem', md: '2.15rem' }, fontWeight: 700,
        letterSpacing: '-0.035em', lineHeight: 1.15, mb: 2,
      }}>
        {title}
      </Typography>

      <Lede>{lede}</Lede>

      {sections.length > 0 && (
        <Box sx={{ mt: 4, mb: 1, borderTop: '1px solid var(--border-soft)', pt: 2 }}>
          <Typography sx={{
            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--text-faint)', mb: 1.25,
          }}>
            On this page
          </Typography>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5,
            maxWidth: `${MEASURE}ch`,
          }}>
            {sections.map((s) => (
              <Box
                key={s.id}
                component="a"
                href={`#${s.id}`}
                sx={{
                  fontSize: '0.9rem', color: 'var(--text-secondary)',
                  textDecoration: 'none', '&:hover': { color: 'var(--text)' },
                }}
              >
                {s.title}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {children}

      <Box sx={{
        mt: 8, pt: 3, borderTop: '1px solid var(--border-soft)',
        display: 'flex', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap',
      }}>
        {prev ? (
          <Box component={Link} to={prev.path} sx={{ textDecoration: 'none', maxWidth: '46%' }}>
            <Typography variant="caption" sx={{ color: 'var(--text-faint)', display: 'block' }}>
              Previous
            </Typography>
            <Typography sx={{ color: 'var(--text)', fontWeight: 600 }}>{prev.title}</Typography>
          </Box>
        ) : <Box />}
        {next && (
          <Box component={Link} to={next.path} sx={{ textDecoration: 'none', textAlign: 'right', maxWidth: '46%' }}>
            <Typography variant="caption" sx={{ color: 'var(--text-faint)', display: 'block' }}>
              Next
            </Typography>
            <Typography sx={{ color: 'var(--text)', fontWeight: 600 }}>{next.title}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
